import { useCallback } from "react";
import cx from "classnames";
import "./Select.scss";

type SelectOptionType = {
  label: string;
  value: string;
  subLabel?: string;
  disabled?: boolean;
  iconName?: string;
};

type SelectProps = {
  label?: string;
  value: string | string[];
  options: SelectOptionType[];
  field: string;
  selectionType?: "single" | "multiple" | "range";
  onChange?: (field: string, value: any) => void;
  className?: string;
  hasSubLabel?: boolean;
  disabled?: boolean;
  disabledMsg?: string;
  // String representation to set the grid template columns
  customTemplateColumns?: string;
};

const Select: React.FC<SelectProps> = ({
  label,
  value,
  options,
  field,
  selectionType = "single",
  onChange,
  className,
  hasSubLabel,
  disabled,
  disabledMsg,
  customTemplateColumns,
}) => {
  const handleSelection = (selectedValue: string) => {
    if (!onChange || disabled) return;

    if (selectionType === "single") {
      onChange(field, selectedValue === value ? null : selectedValue);
    } else if (selectionType === "range") {
      if (!isNaN(Number(selectedValue))) {
        const selectedNumeric = Number(selectedValue);

        const numericOptions = options
          .filter((opt) => !isNaN(Number(opt.value)))
          .map((opt) => ({ ...opt, numericValue: Number(opt.value) }))
          .filter((opt) => opt.numericValue <= selectedNumeric)
          .map((opt) => opt.value);

        onChange(field, numericOptions);
      } else {
        // Fallback to singular if its NaN
        onChange(field, selectedValue === value ? null : selectedValue);
      }
    } else {
      const currentValues = Array.isArray(value) ? value : value ? [value] : [];
      if (currentValues.includes(selectedValue)) {
        // Remove value
        const newValues = currentValues.filter((v) => v !== selectedValue);
        onChange(field, newValues.length === 0 ? null : newValues);
      } else {
        // Add value
        onChange(field, [...currentValues, selectedValue]);
      }
    }
  };

  const isSelected = useCallback(
    (optionValue: string) => {
      if (selectionType === "single") {
        return value === optionValue;
      }
      return Array.isArray(value)
        ? value.includes(optionValue)
        : value === optionValue;
    },
    [selectionType, value],
  );

  return (
    <div className={cx("Select", className, { "Select--Disabled": disabled })}>
      {label && (
        <div className="Select__Label">
          {label}
          {disabled && disabledMsg && (
            <div className="Select__DisabledMessage">{disabledMsg}</div>
          )}
        </div>
      )}
      <div
        className="Select__Options"
        style={{
          gridTemplateColumns: customTemplateColumns,
        }}
      >
        {options.map((option) => (
          <button
            key={option.value}
            className={cx(
              hasSubLabel ? "Select__DetailedOption" : "Select__Option",
              {
                "Select__DetailedOption--Selected":
                  hasSubLabel && isSelected(option.value),
                "Select__Option--Selected":
                  !hasSubLabel && isSelected(option.value),
                "Select__Option--Disabled":
                  (option.disabled || disabled) && !isSelected(option.value),
                "Select__Option--SelectedDisabled":
                  (option.disabled || disabled) && isSelected(option.value),
              },
            )}
            onClick={() => !option.disabled && handleSelection(option.value)}
            type="button"
            disabled={option.disabled || disabled}
          >
            <span
              className={cx({
                Select__DetailedOption__Label: hasSubLabel,
                Select__Option__Label: !hasSubLabel,
                "Select__Option__Label--Selected":
                  !hasSubLabel && isSelected(option.value),
                "Select__DetailedOption__Label--Selected":
                  hasSubLabel && isSelected(option.value),
              })}
            >
              {option.label}
            </span>
            {hasSubLabel && option.subLabel && (
              <span
                className={cx("Select__DetailedOption__SubLabel", {
                  "Select__DetailedOption__SubLabel--Selected": isSelected(
                    option.value,
                  ),
                })}
              >
                {option.subLabel}
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

export { Select };
